import { useCallback, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { Schema } from "effect";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { reportOperationalError } from "../../../../../lib/operational-error-telemetry";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { useAudio } from "../../../../../lib/use-audio";
import { normalizeDateOfBirthForApi } from "../../../components/DateOfBirthInput";
import { PatientSearchSchema, type PatientSearchData } from "../schema";
import { useCheckInSend } from "../context";
import type { CheckInPatientLookupSearchState } from "./check-in-patient-lookup-model";
import { useTranslation } from "react-i18next";

const CHECKIN_PATIENT_LOOKUP_DEFAULT_VALUES: PatientSearchData = {
  lastname: "",
  dob: "",
  firstname: "",
};

/**
 * Coordinates manual check-in patient lookup and resulting machine events.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * function ExampleLookupButton() {
 *   const lookup = useCheckInPatientLookup();
 *   return <button onClick={lookup.handleConfirm}>Confirm</button>;
 * }
 * ```
 */
export function useCheckInPatientLookup() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const [searchState, setSearchState] =
    useState<CheckInPatientLookupSearchState>({
      status: "idle",
    });

  useAudio("checkIn-patientLookup");

  const form = useForm({
    defaultValues: CHECKIN_PATIENT_LOOKUP_DEFAULT_VALUES,
    validators: {
      onChange: Schema.toStandardSchemaV1(PatientSearchSchema),
    },
    onSubmit: async ({ value }) => {
      setSearchState({ status: "searching" });
      try {
        const result = await docCirrusRpcClient.frontdesk.searchPatient({
          lastname: value.lastname,
          dob: normalizeDateOfBirthForApi(value.dob),
          firstname: value.firstname || undefined,
        });

        if (result.status === "FOUND") {
          if (!result.insurance || result.insurance === "UNKNOWN") {
            void reportOperationalError({
              action: "patient-search.insurance-unrecognized",
              severity: "error",
              source: "invariant",
              component: "useCheckInPatientLookup",
              error: new Error(
                "Patient search returned no supported insurance type",
              ),
              context: {
                insuranceRecognized: false,
              },
            });
            setSearchState({
              status: "error",
              message: t("self-service.check-in.patient-lookup.error"),
            });
            return;
          }

          setSearchState({
            status: "found",
            patientId: result.patientId,
            insurance: result.insurance,
          });
        } else if (result.status === "AMBIGUOUS") {
          setSearchState({ status: "ambiguous", hint: result.hint });
        } else {
          setSearchState({ status: "not_found" });
        }
      } catch (error) {
        frontdeskDiagnosticLogger.error("Patient search failed", {
          error,
          hasLastname: Boolean(value.lastname),
          hasDob: Boolean(value.dob),
          hasFirstname: Boolean(value.firstname),
        });
        setSearchState({
          status: "error",
          message: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
  });

  const handleConfirm = useCallback(() => {
    if (
      searchState.status === "found" &&
      searchState.patientId &&
      searchState.insurance &&
      searchState.insurance !== "UNKNOWN"
    ) {
      send({
        type: "PATIENT_CONFIRMED",
        patientId: searchState.patientId,
        insurance: searchState.insurance,
      });
    }
  }, [searchState, send]);

  const handleRegister = useCallback(() => {
    send({ type: "REGISTER" });
  }, [send]);

  const needsFirstname =
    searchState.status === "ambiguous" && searchState.hint === "firstname";

  return {
    form,
    handleConfirm,
    handleRegister,
    isSearching: searchState.status === "searching",
    needsFirstname,
    searchState,
  };
}

/**
 * TanStack Form API for the check-in patient lookup form.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * type LookupForm = CheckInPatientLookupFormApi;
 * ```
 */
export type CheckInPatientLookupFormApi = ReturnType<
  typeof useCheckInPatientLookup
>["form"];
